/**
 * recurrence.ts — RFC 5545 RRULE build / parse + Google-style presets.
 *
 * Framework-free (no React, no i18n). The calendar composer's recurrence picker
 * uses these to turn UI state into the ONE canonical wire representation shared
 * across the app — an RRULE body string (no ``RRULE:`` prefix), e.g.
 * ``"FREQ=WEEKLY;INTERVAL=2;BYDAY=TU;COUNT=10"``. The backend consumes it as-is
 * for Google Calendar and translates it to Microsoft Graph's structured
 * ``PatternedRecurrence`` — so the FE only ever emits the RRULE shapes that
 * translator understands (DAILY / WEEKLY[+BYDAY] / MONTHLY[absolute|relative] /
 * YEARLY, with INTERVAL and COUNT / UNTIL). Empty string = one-off event.
 */

export type RecurrenceFreq = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
export type RecurrenceEndKind = "never" | "until" | "count";

export interface RecurrenceRule {
  readonly freq: RecurrenceFreq;
  /** ``>= 1``. "Every N days/weeks/…". */
  readonly interval: number;
  /** Weekly: ``["MO","TU"]``. Relative monthly/yearly: one ordinal-prefixed
   *  code, e.g. ``["3TU"]`` (third Tuesday) or ``["-1FR"]`` (last Friday). */
  readonly byDay: readonly string[];
  /** Absolute monthly / yearly day-of-month (1–31), else ``null``. */
  readonly byMonthDay: number | null;
  /** Yearly month (1–12), else ``null``. */
  readonly byMonth: number | null;
  readonly endKind: RecurrenceEndKind;
  /** ``"YYYY-MM-DD"`` when ``endKind === "until"``. */
  readonly until: string | null;
  /** Occurrence count when ``endKind === "count"``. */
  readonly count: number | null;
}

/** RRULE weekday codes indexed by ``Date.getDay()`` (0 = Sunday). */
export const RRULE_WEEKDAYS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;
export const WEEKDAY_CODE_ORDER = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"] as const;

/** RRULE code for a date's weekday (e.g. a Tuesday → ``"TU"``). */
export function weekdayCode(d: Date): string {
  return RRULE_WEEKDAYS[d.getDay()] ?? "MO";
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Build an RRULE body string (no ``RRULE:`` prefix). ``""`` for a null rule. */
export function buildRRule(rule: RecurrenceRule | null): string {
  if (!rule) return "";
  const parts: string[] = [`FREQ=${rule.freq}`];
  if (rule.interval > 1) parts.push(`INTERVAL=${rule.interval}`);
  if (rule.byDay.length > 0) parts.push(`BYDAY=${rule.byDay.join(",")}`);
  if (rule.byMonthDay != null) parts.push(`BYMONTHDAY=${rule.byMonthDay}`);
  if (rule.byMonth != null) parts.push(`BYMONTH=${rule.byMonth}`);
  if (rule.endKind === "count" && rule.count != null) {
    parts.push(`COUNT=${rule.count}`);
  } else if (rule.endKind === "until" && rule.until) {
    // Emit an end-of-day UTC UNTIL (the BE reads the leading YYYYMMDD → date).
    const [y, m, d] = rule.until.split("-");
    parts.push(`UNTIL=${y}${m}${d}T235959Z`);
  }
  return parts.join(";");
}

/** Parse an RRULE body back into a :type:`RecurrenceRule` (``null`` if empty /
 *  no ``FREQ`` / unsupported ``FREQ``). Tolerates a leading ``RRULE:`` prefix. */
export function parseRRule(input: string | null | undefined): RecurrenceRule | null {
  if (!input) return null;
  let body = input.trim();
  if (body.toUpperCase().startsWith("RRULE:")) body = body.slice(body.indexOf(":") + 1);
  if (!body) return null;

  const kv: Record<string, string> = {};
  for (const part of body.split(";")) {
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    kv[part.slice(0, eq).trim().toUpperCase()] = part.slice(eq + 1).trim();
  }

  const freq = (kv.FREQ ?? "").toUpperCase();
  if (freq !== "DAILY" && freq !== "WEEKLY" && freq !== "MONTHLY" && freq !== "YEARLY") {
    return null;
  }

  const interval = Math.max(1, Number.parseInt(kv.INTERVAL ?? "1", 10) || 1);
  const byDay = kv.BYDAY ? kv.BYDAY.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) : [];
  const byMonthDay = kv.BYMONTHDAY ? Number.parseInt(kv.BYMONTHDAY, 10) : null;
  const byMonth = kv.BYMONTH ? Number.parseInt(kv.BYMONTH, 10) : null;

  let endKind: RecurrenceEndKind = "never";
  let until: string | null = null;
  let count: number | null = null;
  if (kv.COUNT) {
    endKind = "count";
    count = Number.parseInt(kv.COUNT, 10) || null;
    if (count == null) endKind = "never";
  } else if (kv.UNTIL) {
    const digits = kv.UNTIL.replace(/[^0-9]/g, "");
    if (digits.length >= 8) {
      until = `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
      endKind = "until";
    }
  }

  return {
    freq,
    interval,
    byDay,
    byMonthDay: Number.isNaN(byMonthDay as number) ? null : byMonthDay,
    byMonth: Number.isNaN(byMonth as number) ? null : byMonth,
    endKind,
    until,
    count,
  };
}

export type RecurrencePresetId =
  | "none"
  | "daily"
  | "weekly"
  | "monthly"
  | "yearly"
  | "weekdays"
  | "custom";

/** Canonical (no end) RRULE for a preset, computed from the event start date.
 *  ``none`` / ``custom`` → ``""`` (custom is driven by an explicit rule). */
export function presetRule(id: RecurrencePresetId, start: Date): string {
  const base = { interval: 1, byDay: [] as string[], byMonthDay: null, byMonth: null, endKind: "never" as const, until: null, count: null };
  switch (id) {
    case "daily":
      return buildRRule({ ...base, freq: "DAILY" });
    case "weekly":
      return buildRRule({ ...base, freq: "WEEKLY", byDay: [weekdayCode(start)] });
    case "weekdays":
      return buildRRule({ ...base, freq: "WEEKLY", byDay: ["MO", "TU", "WE", "TH", "FR"] });
    case "monthly":
      return buildRRule({ ...base, freq: "MONTHLY", byMonthDay: start.getDate() });
    case "yearly":
      return buildRRule({ ...base, freq: "YEARLY", byMonth: start.getMonth() + 1, byMonthDay: start.getDate() });
    default:
      return "";
  }
}

/** Which preset an RRULE corresponds to (for the picker's current selection).
 *  ``"none"`` for empty, ``"custom"`` when it matches no preset. End clauses
 *  (COUNT / UNTIL) force ``"custom"`` — presets are open-ended by definition. */
export function matchPreset(rrule: string, start: Date): RecurrencePresetId {
  if (!rrule.trim()) return "none";
  const rule = parseRRule(rrule);
  if (!rule) return "none";
  if (rule.endKind !== "never") return "custom";
  const canonical = buildRRule(rule);
  for (const id of ["daily", "weekly", "weekdays", "monthly", "yearly"] as const) {
    if (presetRule(id, start) === canonical) return id;
  }
  return "custom";
}

/** ``"YYYY-MM-DD"`` for a Date in LOCAL time (matches the composer's date wire). */
export function toYmd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
